import '../title';
import { getTitleLevelFromType } from '../title/utils';
import { defineCustomElement, getAttribute, getBooleanAttribute, getLiteralAttribute, getRect, isAttrEqual, isAttrTrue, NectaryElement, setClass, updateAttribute, updateBooleanAttribute, updateLiteralAttribute } from '../utils';
import { DEFAULT_SIZE, sizeValues } from '../utils/size';
const templateHTML = '<style>:host{display:block}#wrapper{display:flex;flex-direction:column;gap:12px;width:100%;height:100%;border:1px solid var(--sinch-comp-segment-color-default-border-initial);border-radius:var(--sinch-comp-segment-shape-radius);box-sizing:border-box;background-color:var(--sinch-comp-segment-color-default-background-initial);padding:8px 0 16px}#header{display:flex;flex-direction:row;align-items:center;height:48px;gap:8px;padding:0 24px}#icon{--sinch-global-size-icon:32px}#caption{min-width:1em;--sinch-comp-title-font:var(--sinch-comp-segment-font-size-m-title)}:host([size="l"]) #caption{--sinch-comp-title-font:var(--sinch-comp-segment-font-size-l-title)}:host([size="s"]) #caption{--sinch-comp-title-font:var(--sinch-comp-segment-font-size-s-title)}#info{display:flex;flex-direction:row;align-items:center;gap:8px;margin-left:auto;align-self:stretch}#info.empty{display:none}#preview{flex:1;flex-basis:auto;height:48px;min-width:0;overflow:hidden;margin-left:24px}#preview.empty{display:none}#info.empty+#collapse{margin-left:auto}#collapse.empty{display:none}#preview:not(.empty)+#info.empty+#collapse:not(.empty),#preview:not(.empty)+#info:not(.empty){margin-left:24px}#content-wrapper{flex:1;min-height:0;overflow-y:auto;padding:4px 24px}#action{display:flex;flex-direction:row;justify-content:flex-end;gap:16px;padding:0 24px}#action.empty{display:none}:host([collapsed]) :is(#content-wrapper,#action){display:none}:host([collapsed]) #wrapper{padding-bottom:8px}::slotted([slot=icon]){margin-right:8px}</style><div id="wrapper"><div id="header"><slot id="icon" name="icon"></slot><sinch-title id="caption" level="3" type="m" ellipsis></sinch-title><div id="preview"><slot name="preview"></slot></div><div id="info"><slot name="info"></slot></div><div id="collapse"><slot name="collapse"></slot></div></div><div id="content-wrapper"><slot name="content"></slot></div><div id="action"><slot name="action"></slot></div></div>';
import { getTitleTypeFromSize } from './utils';
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-segment', class extends NectaryElement {
  #$caption;
  #$previewSlot;
  #$previewWrapper;
  #$infoSlot;
  #$infoWrapper;
  #$collapseSlot;
  #$collapseWrapper;
  #$actionSlot;
  #$actionWrapper;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$caption = shadowRoot.querySelector('#caption');
    this.#$previewSlot = shadowRoot.querySelector('slot[name="preview"]');
    this.#$infoSlot = shadowRoot.querySelector('slot[name="info"]');
    this.#$collapseSlot = shadowRoot.querySelector('slot[name="collapse"]');
    this.#$actionSlot = shadowRoot.querySelector('slot[name="action"]');
    this.#$previewWrapper = shadowRoot.querySelector('#preview');
    this.#$infoWrapper = shadowRoot.querySelector('#info');
    this.#$collapseWrapper = shadowRoot.querySelector('#collapse');
    this.#$actionWrapper = shadowRoot.querySelector('#action');
  }
  connectedCallback() {
    this.#$previewSlot.addEventListener('slotchange', this.#onPreviewSlotChange);
    this.#$infoSlot.addEventListener('slotchange', this.#onInfoSlotChange);
    this.#$collapseSlot.addEventListener('slotchange', this.#onCollapseSlotChange);
    this.#$actionSlot.addEventListener('slotchange', this.#onActionSlotChange);
    this.#onPreviewSlotChange();
    this.#onInfoSlotChange();
    this.#onCollapseSlotChange();
    this.#onActionSlotChange();
  }
  disconnectedCallback() {
    this.#$previewSlot.removeEventListener('slotchange', this.#onPreviewSlotChange);
    this.#$infoSlot.removeEventListener('slotchange', this.#onInfoSlotChange);
    this.#$collapseSlot.removeEventListener('slotchange', this.#onCollapseSlotChange);
    this.#$actionSlot.removeEventListener('slotchange', this.#onActionSlotChange);
  }
  static get observedAttributes() {
    return ['caption', 'collapsed', 'size'];
  }
  attributeChangedCallback(name, oldVal, newVal) {
    switch (name) {
      case 'caption':
        {
          updateAttribute(this.#$caption, 'text', newVal);
          break;
        }
      case 'collapsed':
        {
          if (isAttrEqual(oldVal, newVal)) {
            return;
          }
          updateBooleanAttribute(this, name, isAttrTrue(newVal));
          break;
        }
      case 'size':
        {
          const titleType = getTitleTypeFromSize(this.size);
          updateAttribute(this.#$caption, 'level', getTitleLevelFromType(titleType));
          break;
        }
    }
  }
  set caption(caption) {
    updateAttribute(this, 'caption', caption);
  }
  get caption() {
    return getAttribute(this, 'caption', '');
  }
  set collapsed(isChecked) {
    updateBooleanAttribute(this, 'collapsed', isChecked);
  }
  get collapsed() {
    return getBooleanAttribute(this, 'collapsed');
  }
  get size() {
    return getLiteralAttribute(this, sizeValues, 'size', DEFAULT_SIZE);
  }
  set size(value) {
    updateLiteralAttribute(this, sizeValues, 'size', value);
  }
  get collapseButtonRect() {
    const $collapseButton = this.#$collapseSlot.assignedElements()[0];
    return $collapseButton != null ? getRect($collapseButton) : null;
  }
  #onPreviewSlotChange = () => {
    setClass(this.#$previewWrapper, 'empty', this.#$previewSlot.assignedElements().length === 0);
  };
  #onInfoSlotChange = () => {
    setClass(this.#$infoWrapper, 'empty', this.#$infoSlot.assignedElements().length === 0);
  };
  #onCollapseSlotChange = () => {
    setClass(this.#$collapseWrapper, 'empty', this.#$collapseSlot.assignedElements().length === 0);
  };
  #onActionSlotChange = () => {
    setClass(this.#$actionWrapper, 'empty', this.#$actionSlot.assignedElements().length === 0);
  };
});